/**
 * Widget registry for the composable dashboard canvas.
 * Both Classic and Desktop canvases reference these definitions.
 */

export interface WidgetDef {
  id: string
  label: string
  /** Default column span in the 12-column desktop grid */
  defaultColSpan: number
  /** Default row span in the desktop grid */
  defaultRowSpan: number
  /** Minimum column span */
  minColSpan: number
  tier: 'free' | 'basic' | 'advanced' | 'unlimited' | 'ultimate'
}

/**
 * Optional per-widget configuration.
 * For embed widgets (chart_embed, sheet_embed, form_embed) this carries
 * the public token and display name of the embedded resource.
 */
export interface WidgetConfig {
  /** Public access token of the embedded Form / Sheet / Chart */
  token?: string
  /** Display name (pre-filled from the resource at time of adding) */
  name?: string
}

export interface WidgetLayout {
  widgetId: string
  visible: boolean
  /** Classic mode: render order (0 = first) */
  order: number
  /** Desktop mode: grid position (1-based) */
  col: number
  row: number
  colSpan: number
  rowSpan: number
  /** Per-widget config (used by embed widget types) */
  config?: WidgetConfig
}

// rowSpan values are based on a 40px grid-auto-rows (2 units = ~80px visible height)
export const WIDGET_DEFS: WidgetDef[] = [
  { id: 'welcome',        label: 'Welcome',          defaultColSpan: 12, defaultRowSpan: 4,  minColSpan: 6,  tier: 'free' },
  { id: 'stats',          label: 'Usage Overview',   defaultColSpan: 12, defaultRowSpan: 6,  minColSpan: 6,  tier: 'free' },
  { id: 'list_chart',     label: 'Entries by List',  defaultColSpan: 8,  defaultRowSpan: 10, minColSpan: 4,  tier: 'free' },
  { id: 'recent_entries', label: 'Recent Entries',   defaultColSpan: 4,  defaultRowSpan: 10, minColSpan: 3,  tier: 'free' },
  { id: 'form_stats',     label: 'Public Forms',     defaultColSpan: 8,  defaultRowSpan: 8,  minColSpan: 4,  tier: 'free' },
  { id: 'quick_actions',  label: 'Quick Actions',    defaultColSpan: 4,  defaultRowSpan: 4,  minColSpan: 3,  tier: 'free' },
  { id: 'capacity',       label: 'Capacity',         defaultColSpan: 4,  defaultRowSpan: 6,  minColSpan: 3,  tier: 'free' },
]

/**
 * Embed widget type defs - kept separate from WIDGET_DEFS so they don't appear
 * in the standard visibility-toggle UI. Embed widgets use dynamic IDs of the
 * form `<type>__<token>` (e.g. "chart_embed__abc123") so multiple instances of
 * the same type can coexist on the canvas.
 */
export const EMBED_WIDGET_DEFS: WidgetDef[] = [
  { id: 'chart_embed', label: 'Chart',  defaultColSpan: 6,  defaultRowSpan: 10, minColSpan: 4, tier: 'free' },
  { id: 'sheet_embed', label: 'Sheet',  defaultColSpan: 6,  defaultRowSpan: 12, minColSpan: 4, tier: 'free' },
  { id: 'form_embed',  label: 'Form',   defaultColSpan: 6,  defaultRowSpan: 14, minColSpan: 4, tier: 'free' },
]

/** The base type prefix for an embed widget ID */
export type EmbedWidgetType = 'chart_embed' | 'sheet_embed' | 'form_embed'

/** Returns true if widgetId starts with one of the embed type prefixes */
export function isEmbedWidget(widgetId: string): boolean {
  return widgetId.startsWith('chart_embed__') || widgetId.startsWith('sheet_embed__') || widgetId.startsWith('form_embed__')
}

/** Derive the base embed type from a dynamic widget ID */
export function embedWidgetType(widgetId: string): EmbedWidgetType | null {
  if (widgetId.startsWith('chart_embed__')) return 'chart_embed'
  if (widgetId.startsWith('sheet_embed__')) return 'sheet_embed'
  if (widgetId.startsWith('form_embed__')) return 'form_embed'
  return null
}

/** Build a stable widget ID for an embed instance */
export function embedWidgetId(type: EmbedWidgetType, token: string): string {
  return `${type}__${token}`
}

/** Extract the public token from an embed widget ID, or null if not an embed widget */
export function embedWidgetToken(widgetId: string): string | null {
  const type = embedWidgetType(widgetId)
  if (!type) return null
  return widgetId.slice(type.length + 2) || null  // skip "<type>__"
}

/** Lookup a def for either a core widget or an embed widget */
export function getWidgetDefForId(widgetId: string): WidgetDef | undefined {
  const coreDef = WIDGET_DEFS.find(d => d.id === widgetId)
  if (coreDef) return coreDef
  const type = embedWidgetType(widgetId)
  if (type) return EMBED_WIDGET_DEFS.find(d => d.id === type)
  return undefined
}

export const WIDGET_IDS = WIDGET_DEFS.map(w => w.id)

export function getWidgetDef(id: string): WidgetDef | undefined {
  return WIDGET_DEFS.find(w => w.id === id)
}

/** Default widget layout for desktop mode (arranged in a sensible starting grid).
 *  Row values use a 40px grid-auto-rows baseline (2 units ≈ 80px). */
export function defaultDesktopWidgets(): WidgetLayout[] {
  return [
    { widgetId: 'welcome',        visible: true,  order: 0, col: 1,  row: 1,  colSpan: 12, rowSpan: 4  },
    { widgetId: 'stats',          visible: true,  order: 1, col: 1,  row: 5,  colSpan: 7,  rowSpan: 6  },
    { widgetId: 'quick_actions',  visible: true,  order: 2, col: 8,  row: 5,  colSpan: 5,  rowSpan: 6  },
    { widgetId: 'list_chart',     visible: true,  order: 3, col: 1,  row: 11, colSpan: 12, rowSpan: 6 },
    { widgetId: 'recent_entries', visible: true,  order: 4, col: 1,  row: 17, colSpan: 4,  rowSpan: 8  },
    { widgetId: 'capacity',       visible: true,  order: 5, col: 5,  row: 17, colSpan: 4,  rowSpan: 8  },
    { widgetId: 'form_stats',     visible: true,  order: 6, col: 9,  row: 17, colSpan: 4,  rowSpan: 8  },
  ]
}

/** Default widget layout for classic mode - compact side-by-side arrangement.
 *  Classic mode uses CSS auto row heights, so rowSpan is unused but kept for
 *  compatibility if the user switches to desktop later. colSpan is tuned
 *  so a 12-col grid packs widgets tightly. */
export function defaultClassicWidgets(): WidgetLayout[] {
  return [
    { widgetId: 'welcome',        visible: true,  order: 0, col: 1, row: 1,  colSpan: 12,  rowSpan: 4  },
    { widgetId: 'stats',          visible: true,  order: 1, col: 1, row: 5,  colSpan: 7,  rowSpan: 6  },
    { widgetId: 'quick_actions',  visible: true,  order: 2, col: 9, row: 21, colSpan: 5,  rowSpan: 4  },
    { widgetId: 'list_chart',     visible: true,  order: 3, col: 1, row: 11, colSpan: 12,  rowSpan: 10 },
    { widgetId: 'recent_entries', visible: true,  order: 4, col: 9, row: 11, colSpan: 4,  rowSpan: 10 },
    { widgetId: 'capacity',       visible: true,  order: 5, col: 9, row: 25, colSpan: 4,  rowSpan: 6  },
    { widgetId: 'form_stats',     visible: true,  order: 6, col: 1, row: 21, colSpan: 4,  rowSpan: 8  },
  ]
}

/** Build default widget layouts from the legacy dashboardStore boolean settings */
export function migrateFromLegacySettings(
  legacy: Record<string, boolean>
): WidgetLayout[] {
  const legacyKeyMap: Record<string, string> = {
    showWelcome:       'welcome',
    showStats:         'stats',
    showListChart:     'list_chart',
    showRecentEntries: 'recent_entries',
    showFormStats:     'form_stats',
    showQuickActions:  'quick_actions',
    showCapacity:      'capacity',
  }
  const base = defaultDesktopWidgets()
  return base.map(w => {
    const legacyKey = Object.entries(legacyKeyMap).find(([, v]) => v === w.widgetId)?.[0]
    return {
      ...w,
      visible: legacyKey ? (legacy[legacyKey] ?? true) : true,
    }
  })
}
